import React from 'react';
import { Leaf, AlertTriangle, Palette } from 'lucide-react';

const COLOR_MAP = {
  vert: '#22c55e',
  rouge: '#ef4444',
  orange: '#f97316',
  jaune: '#eab308',
  blanc: '#e5e7eb',
  brun: '#92400e',
  violet: '#a855f7',
  rose: '#ec4899',
  beige: '#d4a574',
  bleu: '#3b82f6',
};

export const VitalityCard = ({ data, language = 'fr', compact = false }) => {
  const t = (fr, en) => (language === 'fr' ? fr : en);

  if (!data || data.total_meals === 0) {
    return (
      <div className="bg-secondary/50 border border-border rounded-xl p-4 text-center">
        <Leaf className="w-6 h-6 text-muted-foreground mx-auto mb-2 opacity-40" />
        <p className="text-sm text-muted-foreground">
          {t('Aucune donnée de vitalité', 'No vitality data')}
        </p>
      </div>
    );
  }

  const pct = data.vitality_percentage;
  const barColor = pct >= 70 ? 'bg-emerald-500' : pct >= 40 ? 'bg-yellow-500' : 'bg-red-500';
  const statusColor = pct >= 70 ? 'text-emerald-500' : pct >= 40 ? 'text-yellow-500' : 'text-red-500';

  if (compact) {
    return (
      <div className="flex items-center gap-3" data-testid="vitality-compact">
        <div className="flex-1">
          <div className="flex items-center justify-between mb-1">
            <span className="text-xs text-muted-foreground flex items-center gap-1">
              <Leaf className="w-3 h-3" />
              {t('Vitalité', 'Vitality')}
            </span>
            <span className={`text-xs font-bold ${statusColor}`}>{pct}%</span>
          </div>
          <div className="h-1.5 bg-secondary rounded-full overflow-hidden">
            <div
              className={`h-full rounded-full transition-all duration-700 ${barColor}`}
              style={{ width: `${Math.min(pct, 100)}%` }}
            />
          </div>
        </div>
        <div className="flex gap-0.5">
          {data.all_colors.slice(0, 5).map((c) => (
            <div
              key={c}
              className="w-3 h-3 rounded-full border border-border/50"
              style={{ backgroundColor: COLOR_MAP[c] || '#666' }}
              title={c}
            />
          ))}
        </div>
      </div>
    );
  }

  return (
    <div
      className="rounded-xl border border-teal-400/40 bg-gradient-to-br from-teal-950/30 via-card to-cyan-950/20 p-5 space-y-4"
      style={{ boxShadow: '0 0 16px rgba(45,212,191,0.42), 0 0 5px rgba(20,184,166,0.32), inset 0 0 0 1px rgba(45,212,191,0.12)' }}
      data-testid="vitality-card"
    >
      {/* Header */}
      <div className="flex items-center justify-between">
        <h3 className="font-heading text-lg uppercase tracking-tight text-foreground flex items-center gap-2">
          <Leaf className="w-5 h-5 text-teal-300" style={{ filter: 'drop-shadow(0 0 6px rgba(45,212,191,0.7))' }} />
          {t('Vitalité', 'Vitality')}
        </h3>
        <span className="text-xs text-teal-300/70 uppercase tracking-widest font-bold">
          {data.period_days}{t(' j', ' d')}
        </span>
      </div>

      {/* Alert banner */}
      {data.needs_alert && (
        <div className="flex items-center gap-2 bg-red-500/10 border border-red-500/30 rounded-lg px-3 py-2">
          <AlertTriangle className="w-4 h-4 text-red-500 flex-shrink-0" />
          <p className="text-xs text-red-400">
            {t(
              `Seulement ${pct}% des repas contiennent des végétaux. Objectif: 40%+`,
              `Only ${pct}% of meals contain vegetables. Goal: 40%+`
            )}
          </p>
        </div>
      )}

      {/* Big score + diversity dots — pure visual, no scroll */}
      <div className="flex items-center justify-between gap-4">
        <div>
          <span className="font-heading text-5xl font-black text-teal-200" style={{ textShadow: '0 0 12px rgba(45,212,191,0.5)' }}>{pct}<span className="text-xl">%</span></span>
          <p className="text-[10px] uppercase tracking-widest text-teal-300/70 font-bold mt-1">
            {data.meals_with_vegetables}/{data.total_meals} {t('repas verts', 'green meals')}
          </p>
        </div>
        {/* Compact neon dots — 1 dot per detected color, glowing in their hue */}
        {data.all_colors.length > 0 && (
          <div className="flex flex-wrap gap-1.5 max-w-[60%] justify-end" data-testid="vitality-color-dots">
            {data.all_colors.map((color) => {
              const c = COLOR_MAP[color] || '#666';
              return (
                <span
                  key={color}
                  className="inline-block w-4 h-4 rounded-full ring-1 ring-white/15"
                  style={{ background: c, boxShadow: `0 0 8px ${c}` }}
                  title={color}
                />
              );
            })}
          </div>
        )}
      </div>

      {/* Compact 3-stat row */}
      <div className="grid grid-cols-3 gap-2">
        <div className="rounded-lg bg-zinc-950/70 ring-1 ring-teal-400/20 p-2 text-center">
          <p className="font-heading text-lg font-black text-teal-200 leading-none">{data.avg_vegetables_g}<span className="text-[10px]">g</span></p>
          <p className="text-[9px] uppercase tracking-widest text-teal-300/70 mt-1">{t('Légumes', 'Veggies')}</p>
        </div>
        <div className="rounded-lg bg-zinc-950/70 ring-1 ring-teal-400/20 p-2 text-center">
          <p className="font-heading text-lg font-black text-teal-200 leading-none">{data.color_count}</p>
          <p className="text-[9px] uppercase tracking-widest text-teal-300/70 mt-1">{t('Couleurs', 'Colors')}</p>
        </div>
        <div className="rounded-lg bg-zinc-950/70 ring-1 ring-teal-400/20 p-2 text-center">
          <p className="font-heading text-lg font-black text-teal-200 leading-none">{data.total_meals}</p>
          <p className="text-[9px] uppercase tracking-widest text-teal-300/70 mt-1">{t('Repas', 'Meals')}</p>
        </div>
      </div>
    </div>
  );
};
